import { defineMessages, type IntlShape } from 'react-intl';

import type { VoiceAssistantError } from '../store/useVoiceAssistantStore';

export const voiceAssistantMessages = defineMessages({
  title: {
    defaultMessage: 'Talk to Heidi',
    id: 'pgEECwqLyC',
    description: 'Title of the voice assistant session panel',
  },
  statusConnecting: {
    defaultMessage: 'Connecting…',
    id: 'xg53YELPg6',
    description: 'Voice session status while the connection is being established',
  },
  statusGreetingPending: {
    // Quiet startup — status only until greeting audio starts (then barge-in).
    defaultMessage: 'Starting…',
    id: '7DEi0BVAK+',
    description:
      'Voice session status after connect while waiting for Heidi’s opening greeting',
  },
  statusError: {
    defaultMessage: 'Something went wrong',
    id: 'frL3ZnJ9fr',
    description: 'Voice session status when the connection failed',
  },
  statusEnded: {
    defaultMessage: 'Ended',
    id: 'NRiLCdKvPo',
    description: 'Voice session status after the session has ended',
  },
  statusSpeaking: {
    defaultMessage: 'Heidi is speaking',
    id: 'nuV/IjROY8',
    description: 'Voice session status while the assistant is speaking',
  },
  statusThinking: {
    defaultMessage: 'Working on it…',
    id: '2qvgUBn3LA',
    description: 'Voice session status while the assistant is processing',
  },
  statusListening: {
    defaultMessage: 'Listening',
    id: '+X09puAyD/',
    description: 'Voice session status while the assistant is listening',
  },
  statusConnected: {
    defaultMessage: 'Connected',
    id: 'oLCGlrkB6P',
    description: 'Voice session status when connected and idle',
  },
  statusDictating: {
    defaultMessage: 'Dictating — Heidi can’t hear you',
    id: 'lwDKQ87/RS',
    description:
      'Voice session status while mic audio is routed to dictation instead of the assistant',
  },
  dictationHeading: {
    defaultMessage: 'Dictating',
    id: '5OSuhh+L2G',
    description: 'Heading of the live dictation capture bar in the Talk to Heidi dock',
  },
  dictationHint: {
    defaultMessage: 'Say “Heidi, stop” when you’re done',
    id: 't8ncHJFiCH',
    description:
      'Hint shown under the live dictation capture explaining the spoken exit phrase',
  },
  startDictation: {
    defaultMessage: 'Start dictation',
    id: 'jldOF472ss',
    description: 'Accessible name for the button that enters dictation mode',
  },
  stopDictation: {
    defaultMessage: 'Stop dictation',
    id: 'W8Ood/oYWp',
    description: 'Accessible name for the button that leaves dictation mode',
  },
  startSession: {
    defaultMessage: 'Talk to Heidi',
    id: 'rdUTHkpOY9',
    description: 'Accessible name for the button that starts a voice session',
  },
  triggerTooltip: {
    defaultMessage: 'Use voice mode',
    id: 'rm/myjhl9U',
    description: 'Composer Talk to Heidi trigger tooltip when voice session is idle',
  },
  stopTranscriptionBeforeStarting: {
    defaultMessage:
      'Stop transcribing first to use Talk to Heidi — they share the microphone.',
    id: 'mPr7Jzk3gp',
    description:
      'Toast shown when Talk to Heidi cannot start because visit transcription is using the microphone',
  },
  endSession: {
    defaultMessage: 'End voice session',
    id: 'E6KKB5xdgj',
    description: 'Accessible name for the button that ends the voice session',
  },
  end: {
    defaultMessage: 'End',
    id: 'r0SKRqWF5B',
    description: 'Label of the button that ends the voice session',
  },
  mute: {
    defaultMessage: 'Mute microphone',
    id: '3vTOZrXlby',
    description: 'Accessible name for the mute button in the voice panel',
  },
  unmute: {
    defaultMessage: 'Unmute microphone',
    id: '9udqrtVyYb',
    description: 'Accessible name for the unmute button in the voice panel',
  },
  downloadDump: {
    defaultMessage: 'Download session log',
    id: 'uoQv2yrsYH',
    description:
      'Accessible name for the button that downloads a text dump of the voice session for debugging',
  },
  sourcesHeadingWithCount: {
    defaultMessage: '{count, plural, one {Source (#)} other {Sources (#)}}',
    id: '4xfE2u/fkr',
    description:
      'Collapsible heading for Evidence citations in Talk to Heidi, including citation count',
  },
  sourceFallbackLabel: {
    defaultMessage: 'Source',
    id: 'omm7i4kIYn',
    description:
      'Fallback label for an Evidence citation with no title, URL, or domain to display',
  },
  keyPoints: {
    defaultMessage: 'Key points',
    id: 'kSKZsY7VLO',
    description: 'Heading for the Evidence key points shown by Talk to Heidi',
  },
  dismissEvidence: {
    defaultMessage: 'Dismiss Key Points',
    id: 'saAwNQqtzA',
    description:
      'Accessible name for the button that closes the Evidence Key Points callout above the Talk to Heidi dock',
  },
  panelAriaLabel: {
    defaultMessage: 'Heidi voice session',
    id: 'tME4P0Z2f+',
    description: 'Accessible name for the voice session side panel',
  },
  approvalTitle: {
    defaultMessage: 'Confirm this action',
    id: 'FqCUex/udp',
    description: 'Title of the chrome confirming a data-changing voice action',
  },
  approvalFallback: {
    defaultMessage: 'Heidi wants to perform an action that changes data.',
    id: 'qjrsQKxypl',
    description:
      'Fallback description in the confirmation dialog when no summary is available',
  },
  approvalConfirm: {
    defaultMessage: 'Confirm',
    id: 'EJWuh0wDzU',
    description: 'Button that approves the pending voice action',
  },
  approvalRecipientEmail: {
    defaultMessage: 'Recipient email',
    id: 'SzmlgZh+eE',
    description: 'Label for editable recipient on voice email approval bar',
  },
  textPlaceholder: {
    defaultMessage: 'Type a message…',
    id: 'tJZ8fJNyTS',
    description: 'Placeholder for the text input in the Talk to Heidi panel',
  },
  sendText: {
    defaultMessage: 'Send message',
    id: 'aLF1wvmRh3',
    description: 'Accessible name for the button that sends a typed message to Heidi',
  },
  enterSpeechMode: {
    defaultMessage: 'Switch to voice input',
    id: 'GREbB+MQ5u',
    description:
      'Accessible name for the button that morphs the Talk to Heidi composer into the speech pill',
  },
  exitSpeechMode: {
    defaultMessage: 'Switch to typing',
    id: 'zbBgI0B3PI',
    description:
      'Accessible name for the button that leaves the speech pill and shows the text composer',
  },
  typeHint: {
    defaultMessage: 'Type',
    id: 'qVd+2B9YBK',
    description:
      'Short hover label on the speech visualiser that opens the text composer',
  },
  dragHandle: {
    defaultMessage: 'Drag to move Talk to Heidi',
    id: 'e3+Uoxn/Ah',
    description: 'Accessible name for the draggable header region of the voice dock',
  },
  guideContinue: {
    defaultMessage: 'Continue',
    id: 'bdpbvi377h',
    description: 'Button that advances the Talk to Heidi on-screen help guide',
  },
  highlightMissing: {
    defaultMessage:
      'I could not find that control on this screen. Try opening the matching page first.',
    id: 'AJjfHxwzYb',
    description:
      'Fallback callout when a Talk to Heidi UI highlight target is not on screen',
  },
  highlightEyebrow: {
    defaultMessage: 'Heidi tip',
    id: 'wrgzia4Icu',
    description: 'Small eyebrow above Talk to Heidi UI highlight / guide callouts',
  },
  activityOpeningOnScreen: {
    defaultMessage: 'Opening on screen',
    id: 'mqjTAhjKdv',
    description: 'Talk to Heidi activity row when opening a session artifact',
  },
  activityOpeningPage: {
    defaultMessage: 'Opening page',
    id: 'LyFwcUyzLt',
    description: 'Talk to Heidi activity row when navigating',
  },
  activityShowingNotice: {
    defaultMessage: 'Showing notice',
    id: '1+jo52UnMc',
    description: 'Talk to Heidi activity row when showing a toast',
  },
  activityHighlighting: {
    defaultMessage: 'Highlighting',
    id: 'dsuwKwBfgy',
    description: 'Talk to Heidi activity row when spotlighting UI',
  },
  activityOpeningTemplatePicker: {
    defaultMessage: 'Opening template picker',
    id: 'JmR9hT4M+A',
    description: 'Talk to Heidi activity row when opening the template picker',
  },
  templatePickerUnavailable: {
    defaultMessage:
      "The template picker isn't available on this screen. Open a session's note first.",
    id: 'scquQtClsr',
    description:
      'Toast shown when Talk to Heidi cannot open the template picker because the target control is not on the current screen',
  },
  micDeniedTitle: {
    defaultMessage: 'Microphone access is blocked',
    id: 'M8Hq1PHZjc',
    description:
      'Heading of the recovery state shown when Talk to Heidi cannot access the microphone',
  },
  micDeniedBodyWeb: {
    defaultMessage:
      "Heidi can't hear you. Allow microphone access for this site in your browser settings, then try again.",
    id: 'aBfYuRq37I',
    description:
      'Recovery guidance in the web app when microphone permission is denied for Talk to Heidi',
  },
  micDeniedBodyMac: {
    defaultMessage:
      'Heidi can’t hear you. Enable microphone access for Heidi in your system’s microphone privacy settings, then try again.',
    id: '4pQRxtIm5y',
    description:
      'Recovery guidance on macOS desktop when microphone permission is denied for Talk to Heidi',
  },
  micDeniedBodyWindows: {
    defaultMessage:
      'Heidi can’t hear you. Enable microphone access for Heidi in your system’s microphone privacy settings, then try again.',
    id: 'J9AMys9Ekg',
    description:
      'Recovery guidance on Windows desktop when microphone permission is denied for Talk to Heidi',
  },
  micDeniedRetry: {
    defaultMessage: 'Try again',
    id: 'f2kKOwZ4Da',
    description:
      'Button that retries starting the Talk to Heidi voice session after a microphone permission error',
  },
  activityLookingUpHelp: {
    defaultMessage: 'Looking up help',
    id: 'jUyVnk/nM/',
    description: 'Talk to Heidi activity row for help lookup',
  },
  activityStartingGuide: {
    defaultMessage: 'Starting guide',
    id: 'RGct37RhEY',
    description: 'Talk to Heidi activity row when starting an on-screen guide',
  },
  activityCheckingStatus: {
    defaultMessage: 'Checking status',
    id: 'xzTDUCQj9C',
    description: 'Talk to Heidi activity row when checking system status',
  },
  activityCreatingSession: {
    defaultMessage: 'Creating session',
    id: 'pR1YZ8dazW',
    description: 'Talk to Heidi activity row when creating a session',
  },
  activityCreatingPatient: {
    defaultMessage: 'Creating patient',
    id: '5YTl3xIfTa',
    description: 'Talk to Heidi activity row when creating a patient',
  },
  activityLinkingPatient: {
    defaultMessage: 'Linking patient',
    id: 'LSmwqsfJcw',
    description: 'Talk to Heidi activity row when linking a patient',
  },
  activityReadingSession: {
    defaultMessage: 'Reading session',
    id: 'qkCRPGU9sL',
    description: 'Talk to Heidi activity row when reading a session',
  },
  activityReadingNote: {
    defaultMessage: 'Reading note',
    id: 'j9aSqeAC9N',
    description: 'Talk to Heidi activity row when reading a note',
  },
  activityReadingPatient: {
    defaultMessage: 'Reading patient',
    id: 'gL1CSWhs0W',
    description: 'Talk to Heidi activity row when reading a patient summary',
  },
  activityWorkingOnIt: {
    defaultMessage: 'Working on it',
    id: 'nTdTQmznJ+',
    description: 'Talk to Heidi activity row while a background agent runs',
  },
  activityOpenedOnScreen: {
    defaultMessage: 'Opened on screen',
    id: 'A4mIMKYjuv',
    description: 'Talk to Heidi completed UI action — opened artifact',
  },
  activityOpenedPage: {
    defaultMessage: 'Opened page',
    id: 'ATdHltrI2O',
    description: 'Talk to Heidi completed UI action — navigated',
  },
  activityShowedNotice: {
    defaultMessage: 'Showed notice',
    id: 'j1B0F/Wbiz',
    description: 'Talk to Heidi completed UI action — toast',
  },
  activityHighlighted: {
    defaultMessage: 'Highlighted',
    id: 'reqrmpXLbR',
    description: 'Talk to Heidi completed UI action — highlight',
  },
  activityOpenedTemplatePicker: {
    defaultMessage: 'Opened template picker',
    id: 'fdYCYMFxrj',
    description: 'Talk to Heidi completed UI action — template picker',
  },
  activityStartedGuide: {
    defaultMessage: 'Started guide',
    id: 'c3sSiluFZl',
    description: 'Talk to Heidi completed UI action — start guide',
  },
  activityUpdatedScreen: {
    defaultMessage: 'Updated screen',
    id: 'Eq1M9B18K4',
    description: 'Talk to Heidi fallback completed UI action label',
  },
  activityWorkingEllipsis: {
    defaultMessage: 'Working…',
    id: 'b5E8CQ+l0n',
    description: 'Talk to Heidi fallback tool activity label',
  },
  activityLabelWithDetail: {
    defaultMessage: '{base} — {detail}',
    id: 'Jslyo2Winc',
    description: 'Talk to Heidi activity label with a short detail suffix',
  },
  activityNeedsConfirmation: {
    defaultMessage: 'Needs confirmation',
    id: 'qbXG81XkkP',
    description: 'Talk to Heidi approval request without a summary',
  },
  activityNeedsConfirmationWithSummary: {
    defaultMessage: 'Needs confirmation — {summary}',
    id: 'RE06f/eidG',
    description: 'Talk to Heidi approval request with a summary',
  },
  activityConfirmed: {
    defaultMessage: 'Confirmed',
    id: 'FagG5d4dBl',
    description: 'Talk to Heidi approval resolved without a summary',
  },
  activityConfirmedWithSummary: {
    defaultMessage: 'Confirmed — {summary}',
    id: 'mY5Kou2ICm',
    description: 'Talk to Heidi approval resolved with a summary',
  },
  minimise: {
    defaultMessage: 'Minimise',
    id: 'lcFQ3ufGH5',
    description:
      'Accessible name for the button that hides the Talk to Heidi dock while keeping the session running',
  },
  endConfirmTitle: {
    defaultMessage: 'End this Talk to Heidi session?',
    id: 'drE6SUzH7x',
    description: 'Title of the dialog confirming that the voice session will be ended',
  },
  endConfirmDescription: {
    defaultMessage:
      'This stops the microphone and clears the conversation. To keep it running in the background, minimise instead.',
    id: '1EiFF4U6eU',
    description:
      'Body of the dialog confirming the voice session will end, mic stopped and transcript cleared',
  },
  endConfirmCancel: {
    defaultMessage: 'Keep talking',
    id: 'QIMv9shlM8',
    description: 'Cancel button on the end-voice-session confirmation dialog',
  },
  endConfirmConfirm: {
    defaultMessage: 'End session',
    id: '2kRktuR/oh',
    description: 'Confirm button that ends the voice session',
  },
  errorNotSignedIn: {
    defaultMessage: 'You are not signed in.',
    id: 'PeplSDdIGl',
    description: 'Voice session error when no auth token is available',
  },
  errorVoiceUnavailable: {
    defaultMessage: 'Voice assistant is unavailable.',
    id: 'scvbQ4Nc0i',
    description: 'Voice session error when the server refuses the connection',
  },
});

/**
 * Display text for the panel's error line: server/recorder text passes
 * through verbatim; local socket failure codes resolve to localized copy.
 */
export const formatVoiceAssistantError = (
  error: VoiceAssistantError,
  intl: IntlShape
): string =>
  typeof error === 'string'
    ? error
    : intl.formatMessage(
        error.kind === 'not_signed_in'
          ? voiceAssistantMessages.errorNotSignedIn
          : voiceAssistantMessages.errorVoiceUnavailable
      );
